/**
 * Render Preview Question
 *
 * Centralized utility for rendering question components in preview mode.
 * Orchestrates the adapter → component → render pipeline.
 */

import React from 'react';
import TeamPick from '@low6-pickem/ui/components/TeamPick';
import PlayerPick from '@low6-pickem/ui/components/PlayerPick';
import PropPick from '@low6-pickem/ui/components/PropPick';
import OverUnderPick from '@low6-pickem/ui/components/OverUnderPick';
import TieBreakerQuestion from '@low6-pickem/ui/components/TieBreakerQuestion';
import CustomPick from '@low6-pickem/ui/components/CustomPick';
import LeaderPick from '@low6-pickem/ui/components/LeaderPick';

import { PreviewI18nProvider } from '@/components/preview/PreviewI18nProvider';
import { getAdapter, isValidQuestionType, type QuestionType } from './adapterRegistry';
import type { RenderableQuestionType } from '../questionRenderTypes';
import type { PreviewQuestionData } from './types';
import type { teamPickAdapter } from './adapters/teamPickAdapter';
import type { playerPickAdapter } from './adapters/playerPickAdapter';
import type { propPickAdapter } from './adapters/propPickAdapter';
import type { overUnderPickAdapter } from './adapters/overUnderPickAdapter';
import type { tiebreakerAdapter } from './adapters/tiebreakerAdapter';
import type { customPickAdapter } from './adapters/customPickAdapter';
import type { leaderPickAdapter } from './adapters/leaderPickAdapter';

/**
 * Union type of all possible adapter return types for type safety
 */
type AdapterProps =
  | ReturnType<typeof teamPickAdapter>
  | ReturnType<typeof playerPickAdapter>
  | ReturnType<typeof propPickAdapter>
  | ReturnType<typeof overUnderPickAdapter>
  | ReturnType<typeof tiebreakerAdapter>
  | ReturnType<typeof customPickAdapter>
  | ReturnType<typeof leaderPickAdapter>;

/**
 * Component map - Maps question types to their React components
 */
export const COMPONENT_MAP = {
  team: TeamPick,
  player: PlayerPick,
  prop: PropPick,
  over_under: OverUnderPick,
  team_over_under: OverUnderPick,
  player_over_under: OverUnderPick,
  tiebreaker: TieBreakerQuestion,
  custom: CustomPick,
  player_leader: LeaderPick,
  team_leader: LeaderPick,
  // Main markets (DKPLAT-500): Moneyline/Spread preview as option-picks (TeamPick);
  // Total as a match-level Over/Under.
  moneyline: TeamPick,
  spread: TeamPick,
  total: OverUnderPick,
  // Exhaustive over RenderableQuestionType at compile time — see adapterRegistry.
} satisfies Record<RenderableQuestionType, React.ElementType>;

/**
 * Renders a question component for preview
 *
 * Pipeline:
 * 1. Validate question type
 * 2. Get adapter for type
 * 3. Transform data → component props
 * 4. Render component
 * 5. Wrap PropPick in PreviewI18nProvider (for useTranslation hook)
 *
 * @param data - Preview question data from admin form
 * @returns Rendered React component or error message
 *
 * @example
 * const preview = renderPreviewQuestion({
 *   questionType: 'team',
 *   questionText: 'Which team will win?',
 *   options: [...],
 *   totalPoints: 10
 * });
 */
export function renderPreviewQuestion(data: PreviewQuestionData): React.ReactNode {
  const questionType = data.questionType as QuestionType;

  // Validate question type
  if (!isValidQuestionType(questionType)) {
    return (
      <div className="text-red-500 p-4 text-center">
        Unsupported question type: <span className="font-mono">{data.questionType}</span>
      </div>
    );
  }

  // Get adapter and component
  const adapter = getAdapter(questionType);
  const Component = COMPONENT_MAP[questionType];

  if (!adapter || !Component) {
    return (
      <div className="text-red-500 p-4 text-center">
        No adapter or component found for type: <span className="font-mono">{questionType}</span>
      </div>
    );
  }

  // Transform data to component props
  let props: AdapterProps;
  try {
    props = adapter(data);
  } catch (error) {
    return (
      <div className="text-red-500 p-4 text-center">
        <p>Error transforming question data:</p>
        <p className="font-mono text-sm mt-2">
          {error instanceof Error ? error.message : String(error)}
        </p>
      </div>
    );
  }

  // PropPick calls useTranslation() from react-i18next, so it needs a real
  // i18next instance in context to resolve its labels.
  if (questionType === 'prop') {
    return (
      <PreviewI18nProvider>
        <Component {...(props as any)} />
      </PreviewI18nProvider>
    );
  }

  // Render component directly
  return <Component {...(props as any)} />;
}
